import React, { useState } from "react";
import { Loader2, Upload, Image as ImageIcon } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { adminUploadImage } from "../lib/api";
import { resolveImage } from "../lib/settings";
import MediaLibraryDialog from "./MediaLibraryDialog";

// Reusable image field: shows a preview, and lets the admin paste a URL,
// upload a new file, or pick an existing image from the Media Library.
const ImagePicker = ({ value, onChange, label = "Image" }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast({ title: "File too large (max 8MB)", variant: "destructive" });
      return;
    }
    setUploading(true);
    try {
      const res = await adminUploadImage(f);
      onChange(res.url);
      toast({ title: "Image uploaded" });
    } catch (err) {
      toast({
        title: "Upload failed",
        description: err?.response?.data?.detail || "Error",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      {label && <Label className="text-[13px]">{label}</Label>}
      <div className={`${label ? "mt-1" : ""} flex items-center gap-3`}>
        <div className="w-24 h-16 rounded-lg overflow-hidden bg-[var(--ri-cream-2)] border border-[var(--ri-border)] shrink-0">
          {value ? (
            <img
              src={resolveImage(value)}
              alt="preview"
              className="w-full h-full object-cover"
              onError={(e) => (e.target.style.opacity = 0.3)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-[var(--ri-muted)] uppercase tracking-widest">
              No image
            </div>
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <Input
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste an image URL, upload, or pick from library"
            className="text-[13px]"
          />
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--ri-red)] cursor-pointer hover:underline">
              {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
              {uploading ? "Uploading..." : "Upload"}
              <input type="file" accept="image/*" className="hidden" onChange={onFile} />
            </label>
            <button
              type="button"
              onClick={() => setLibraryOpen(true)}
              className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--ri-red)] hover:underline"
            >
              <ImageIcon size={13} /> Library
            </button>
          </div>
        </div>
      </div>
      <MediaLibraryDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        onSelect={(url) => onChange(url)}
      />
    </div>
  );
};

export default ImagePicker;
